import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Divider,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Paper,
  Tooltip,
  CircularProgress,
  Alert,
  Badge,
} from '@mui/material';
import {
  Close as CloseIcon,
  PhotoCamera as PhotoIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Description as ContractIcon,
  AccountBalance as BankIcon,
  MonetizationOn as PaieIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { employeesAPI, employeePrimesAPI, API_BASE_URL } from '../../../services/api';
import {
  Employee,
  EmployeeCreate,
  EmployeeUpdate,
  TYPE_CONTRAT_OPTIONS,
  STATUT_EMPLOYE_OPTIONS,
  SITUATION_FAMILIALE_OPTIONS,
  MOTIF_SORTIE_OPTIONS,
  TypeContrat,
  StatutEmploye,
  SituationFamiliale,
  MotifSortie,
  EmployeeDocument,
  DocumentType,
  DOCUMENT_TYPE_OPTIONS,
} from '../../../types/employee';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { getFullFileUrl } from '../../../utils/fileUrl';
import PriceInput, { parsePriceValue } from '../../../components/PriceInput';
import useAlert from '../../../hooks/useAlert';
import useFormDraft from '../../../hooks/useFormDraft';
import AlertDialog from '../../../components/AlertDialog';
import ImagePreviewModal from '../../../components/chat/ImagePreviewModal';
import PaieTab from '../components/PaieTab';
import {
  SlideUpTransition,
  useMinimizable,
  MinimizeButton,
  MinimizedBar,
} from '../../../components/MinimizableDialog';

const HR_COLOR = '#00897B';

// Moroccan bank codes mapping (3-digit code from RIB -> Bank name)
const MOROCCAN_BANK_CODES: Record<string, string> = {
  '001': 'Bank Al-Maghrib (BAM)',
  '002': 'Arab Bank (AB)',
  '005': 'Union Marocaine de Banques (UMB)',
  '007': 'Attijariwafa Bank',
  '009': 'Société de Banque et de Crédit (SBC)',
  '011': 'BMCE Bank',
  '013': 'BMCI',
  '019': 'Wafabank (WB)',
  '021': 'Crédit Du Maroc (CDM)',
  '022': 'Société Générale Maroc (SGMB)',
  '023': 'ABN AMRO',
  '025': 'BMAO',
  '026': 'Union Bancaire (UNIB)',
  '028': 'Citibank',
  '031': 'SMDC',
  '032': 'BEX',
  '054': 'CDG',
  '101': 'Banque Populaire Agadir (BPA)',
  '105': 'Banque Populaire El Jadida (BPH)',
  '109': 'Banque Populaire Beni Mellal (BPBM)',
  '117': 'Banque Populaire Errachidia (BPE)',
  '127': 'Banque Populaire Fès (BPF)',
  '133': 'Banque Populaire Gharb (BPG)',
  '140': 'Banque Populaire Kenitra (BPKH)',
  '143': 'Banque Populaire Laâyoune (BPL)',
  '145': 'Banque Populaire Marrakech (BPM)',
  '148': 'Banque Populaire Meknès (BPMK)',
  '149': 'Banque Populaire Rif (BPRI)',
  '150': 'Banque Populaire Nador (BPN)',
  '155': 'Banque Populaire Oujda (BPOU)',
  '157': 'Banque Populaire Oriental (BPO)',
  '159': 'Banque Populaire Safi (BPS)',
  '164': 'Banque Populaire Tanger (BPTA)',
  '169': 'Banque Populaire Taza (BPTZ)',
  '172': 'Banque Populaire Tétouan (BPTE)',
  '175': 'Banque Populaire Tiznit (BPTI)',
  '178': 'Banque Populaire Casablanca (BPCA)',
  '181': 'Banque Populaire Rabat (BPR)',
  '190': 'Banque Centrale Populaire (BCP)',
  '195': 'Banque Populaire Centre (BPCE)',
  '197': 'Banque Populaire Centre-Sud (BPCS)',
  '205': 'BNDE',
  '210': 'Caisse de Dépôt et de Gestion (CDG)',
  '225': 'Crédit Agricole du Maroc (CAM)',
  '230': 'CIH Bank',
  '310': 'Trésorerie Générale du Royaume (TGR)',
  '350': 'CCP (Poste Maroc)',
};

// Function to detect bank from RIB
const detectBankFromRIB = (rib: string): string | null => {
  // Clean the RIB (remove spaces and non-digits)
  const cleanRIB = rib.replace(/\D/g, '');

  // RIB should have at least 3 digits for the bank code
  if (cleanRIB.length < 3) return null;

  // Get the first 3 digits (bank code)
  const bankCode = cleanRIB.substring(0, 3);

  return MOROCCAN_BANK_CODES[bankCode] || null;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    hidden={value !== index}
    style={{ paddingTop: 16, display: value === index ? 'block' : 'none' }}
  >
    {children}
  </div>
);

interface EmployeeFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  employee: Employee | null;
  viewMode?: boolean;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const EmployeeFormDialog: React.FC<EmployeeFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  employee,
  viewMode = false,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(!viewMode);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { alertState, showSuccess, showError, hideAlert } = useAlert();
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );

  // Form state
  const [formData, setFormData] = useState<EmployeeCreate>({
    nom_complet: '',
    date_naissance: null,
    lieu_naissance: '',
    nationalite: 'MAROCAIN',
    cin: '',
    adresse: '',
    telephone: '',
    situation_familiale: 'CELIBATAIRE',
    nbr_enfants: 0,
    nom_complet_ar: '',
    lieu_naissance_ar: '',
    adresse_ar: '',
    photo_url: null,
    date_embauche: new Date().toISOString().split('T')[0],
    fonction: '',
    statut: 'ACTIF',
    type_contrat: 'CDI',
    duree_contrat_mois: null,
    date_fin_contrat: null,
    relance_contrat: false,
    date_sortie: null,
    motif_sortie: null,
    numero_cnss: '',
    banque: '',
    rib: '',
    salaire_base: null,
    salaire_net: null,
    declaration_journaliere: null,
    // Payroll calculated fields
    salaire_brut_global: null,
    salaire_brut_imposable: null,
    total_cotisations: null,
    frais_professionnel: null,
    salaire_net_imposable: null,
    ir_brut: null,
    deduction_charge_famille: null,
    ir_net: null,
  });

  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);
  const [nextMatricule, setNextMatricule] = useState<string>('');

  // Document upload state
  const [documents, setDocuments] = useState<EmployeeDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [newDocumentType, setNewDocumentType] = useState<DocumentType>('CONTRAT');
  const [newDocumentTitle, setNewDocumentTitle] = useState('');
  const [newDocumentDescription, setNewDocumentDescription] = useState('');
  const documentInputRef = useRef<HTMLInputElement>(null);

  // Pending documents for new employees (uploaded after creation)
  interface PendingDocument {
    id: string; // temporary ID for UI
    file: File;
    documentType: DocumentType;
    title: string;
    description: string;
  }
  const [pendingDocuments, setPendingDocuments] = useState<PendingDocument[]>([]);

  // PAIE primes state
  const [primesExonerees, setPrimesExonerees] = useState<{ id?: number | null; prime_id: number; montant: number; date_debut?: string | null; date_fin?: string | null }[]>([]);
  const [primesNonExonerees, setPrimesNonExonerees] = useState<{ id?: number | null; prime_id: number; montant: number; nombre_jours?: number | null; mois?: number | null; annee?: number | null; date_debut?: string | null; date_fin?: string | null }[]>([]);
  const [cotisationsSociales, setCotisationsSociales] = useState<{ cotisation_id: number }[]>([]);

  // Draft caching — only for new employees (never when editing an existing row).
  // Non-serializable state (pendingPhotoFile, photoPreview, pendingDocuments File
  // objects) is intentionally excluded — it does not survive JSON round-trips.
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: EmployeeCreate;
    primesExonerees: { id?: number | null; prime_id: number; montant: number; date_debut?: string | null; date_fin?: string | null }[];
    primesNonExonerees: { id?: number | null; prime_id: number; montant: number; nombre_jours?: number | null; mois?: number | null; annee?: number | null; date_debut?: string | null; date_fin?: string | null }[];
    cotisationsSociales: { cotisation_id: number }[];
  }>('employee', !employee);

  // Validation state - tracks which fields have been touched/validated
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showAllErrors, setShowAllErrors] = useState(false);

  // Validation rules - ALL FIELDS REQUIRED
  const getFieldError = (field: string): string | null => {
    switch (field) {
      // Tab 0: Personnel
      case 'nom_complet':
        if (!formData.nom_complet?.trim()) return 'Le nom complet est requis';
        break;
      case 'date_naissance':
        if (!formData.date_naissance) return 'La date de naissance est requise';
        break;
      case 'cin':
        if (!formData.cin?.trim()) return 'Le CIN est requis';
        break;
      case 'situation_familiale':
        if (!formData.situation_familiale) return 'La situation familiale est requise';
        break;
      case 'nbr_enfants':
        if (formData.nbr_enfants === null || formData.nbr_enfants === undefined) return 'Le nombre d\'enfants est requis';
        break;
      case 'telephone':
        if (!formData.telephone?.trim()) return 'Le contact est requis';
        break;
      case 'adresse':
        if (!formData.adresse?.trim()) return 'L\'adresse est requise';
        break;

      // Tab 1: Professionnel
      case 'date_embauche':
        if (!formData.date_embauche) return 'La date d\'embauche est requise';
        break;
      case 'fonction':
        if (!formData.fonction?.trim()) return 'La fonction est requise';
        break;
      case 'statut':
        if (!formData.statut) return 'Le statut est requis';
        break;
      case 'numero_cnss':
        if (!formData.numero_cnss?.trim()) return 'Le numéro CNSS est requis';
        break;

      // Tab 2: Contrat
      case 'type_contrat':
        if (!formData.type_contrat) return 'Le type de contrat est requis';
        break;
      case 'duree_contrat_mois':
        if (formData.type_contrat !== 'CDI' && !formData.duree_contrat_mois) return 'La durée du contrat est requise';
        break;
      case 'date_fin_contrat':
        if (formData.type_contrat !== 'CDI' && !formData.date_fin_contrat) return 'La date de fin de contrat est requise';
        break;

      // Tab 3: Bancaire & Salaire
      case 'banque':
        if (!formData.banque?.trim()) return 'La banque est requise';
        break;
      case 'rib':
        if (!formData.rib?.trim()) return 'Le RIB est requis';
        break;
      case 'salaire_base':
        if (!formData.salaire_base) return 'Le salaire de base est requis';
        break;
      case 'salaire_net':
        if (!formData.salaire_net) return 'Le salaire net est requis';
        break;
      case 'declaration_journaliere':
        if (!formData.declaration_journaliere) return 'La déclaration journalière est requise';
        break;
    }
    return null;
  };

  // Check if field has error and should show it
  const hasError = (field: string): boolean => {
    const error = getFieldError(field);
    return !!error && (touched[field] || showAllErrors);
  };

  // Get error message for field
  const getErrorText = (field: string): string => {
    if (!hasError(field)) return '';
    return getFieldError(field) || '';
  };

  // Mark field as touched on blur
  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Get tab error indicators
  const getTabErrors = () => {
    const errors: Record<number, boolean> = {
      0: false, // Personnel tab
      1: false, // Contrat & Professionnel tab (merged)
      2: false, // Bancaire tab
      3: false, // PAIE tab (no required fields)
    };

    // Check Personnel tab
    const personnelFields = ['nom_complet', 'date_naissance', 'cin', 'situation_familiale', 'nbr_enfants', 'telephone', 'adresse'];
    if (personnelFields.some(f => hasError(f))) errors[0] = true;

    // Check Contrat & Professionnel tab (merged)
    const contratFields = ['date_embauche', 'fonction', 'statut', 'numero_cnss', 'type_contrat', 'duree_contrat_mois', 'date_fin_contrat'];
    if (contratFields.some(f => hasError(f))) errors[1] = true;

    // Check Bancaire tab
    const bancaireFields = ['banque', 'rib'];
    if (bancaireFields.some(f => hasError(f))) errors[2] = true;

    // Check PAIE tab
    const paieFields = ['salaire_base', 'salaire_net', 'declaration_journaliere'];
    if (paieFields.some(f => hasError(f))) errors[3] = true;

    return errors;
  };

  const tabErrors = getTabErrors();

  // Fetch next matricule when opening dialog for new employee or when contract type changes
  useEffect(() => {
    if (open && !employee) {
      employeesAPI.getNextMatricule(formData.type_contrat)
        .then((data) => setNextMatricule(data.matricule))
        .catch((error) => console.error('Failed to fetch next matricule:', error));
    }
  }, [open, employee, formData.type_contrat]);

  useEffect(() => {
    if (employee) {
      setFormData({
        nom_complet: employee.nom_complet,
        date_naissance: employee.date_naissance,
        lieu_naissance: employee.lieu_naissance || '',
        nationalite: employee.nationalite || 'MAROCAIN',
        cin: employee.cin || '',
        adresse: employee.adresse || '',
        telephone: employee.telephone || '',
        situation_familiale: employee.situation_familiale,
        nbr_enfants: employee.nbr_enfants,
        nom_complet_ar: employee.nom_complet_ar || '',
        lieu_naissance_ar: employee.lieu_naissance_ar || '',
        adresse_ar: employee.adresse_ar || '',
        photo_url: employee.photo_url,
        date_embauche: employee.date_embauche,
        fonction: employee.fonction || '',
        statut: employee.statut,
        type_contrat: employee.type_contrat,
        duree_contrat_mois: employee.duree_contrat_mois,
        date_fin_contrat: employee.date_fin_contrat,
        relance_contrat: employee.relance_contrat,
        date_sortie: employee.date_sortie,
        motif_sortie: employee.motif_sortie,
        numero_cnss: employee.numero_cnss || '',
        banque: employee.banque || '',
        rib: employee.rib || '',
        salaire_base: employee.salaire_base,
        salaire_net: employee.salaire_net,
        declaration_journaliere: employee.declaration_journaliere,
        // Payroll calculated fields
        salaire_brut_global: employee.salaire_brut_global,
        salaire_brut_imposable: employee.salaire_brut_imposable,
        total_cotisations: employee.total_cotisations,
        frais_professionnel: employee.frais_professionnel,
        salaire_net_imposable: employee.salaire_net_imposable,
        ir_brut: employee.ir_brut,
        deduction_charge_famille: employee.deduction_charge_famille,
        ir_net: employee.ir_net,
      });
      setPhotoUrl(employee.photo_url);
      setIsEditing(!viewMode);
    } else {
      // New employee — repopulate from a draft if one was stashed by an
      // accidental backdrop/Escape close, otherwise do the blank reset.
      const draft = loadDraft();
      if (draft) {
        setFormData(draft.formData);
        setPrimesExonerees(draft.primesExonerees);
        setPrimesNonExonerees(draft.primesNonExonerees);
        setCotisationsSociales(draft.cotisationsSociales);
      } else {
        setFormData({
          nom_complet: '',
          date_naissance: null,
          lieu_naissance: '',
          nationalite: 'MAROCAIN',
          cin: '',
          adresse: '',
          telephone: '',
          situation_familiale: 'CELIBATAIRE',
          nbr_enfants: 0,
          nom_complet_ar: '',
          lieu_naissance_ar: '',
          adresse_ar: '',
          photo_url: null,
          date_embauche: new Date().toISOString().split('T')[0],
          fonction: '',
          statut: 'ACTIF',
          type_contrat: 'CDI',
          duree_contrat_mois: null,
          date_fin_contrat: null,
          relance_contrat: false,
          date_sortie: null,
          motif_sortie: null,
          numero_cnss: '',
          banque: '',
          rib: '',
          salaire_base: null,
          salaire_net: null,
          declaration_journaliere: null,
          // Payroll calculated fields
          salaire_brut_global: null,
          salaire_brut_imposable: null,
          total_cotisations: null,
          frais_professionnel: null,
          salaire_net_imposable: null,
          ir_brut: null,
          deduction_charge_famille: null,
          ir_net: null,
        });
        setPrimesExonerees([]);
        setPrimesNonExonerees([]);
        setCotisationsSociales([]);
      }
      setPhotoUrl(null);
      setPendingPhotoFile(null);
      setPhotoPreview(null);
      setIsEditing(true);
    }
    setTabValue(0);
    // Reset validation state
    setTouched({});
    setShowAllErrors(false);
    // Reset document upload form (File objects are never cached)
    setDocuments([]);
    setPendingDocuments([]);
    setNewDocumentType('CONTRAT');
    setNewDocumentTitle('');
    setNewDocumentDescription('');
    // Reset PAIE primes for the edit branch (create branch handled above)
    if (employee) {
      setPrimesExonerees([]);
      setPrimesNonExonerees([]);
    }
    // Reset alert state
    hideAlert();
  }, [employee, viewMode, open, hideAlert, loadDraft]);

  // Fetch documents when opening dialog for an existing employee
  useEffect(() => {
    const fetchDocuments = async () => {
      if (open && employee?.id) {
        setDocumentsLoading(true);
        try {
          const response = await employeesAPI.getDocuments(employee.id);
          setDocuments(response.documents);
        } catch (error: any) {
          console.error('Failed to fetch documents:', error);
        } finally {
          setDocumentsLoading(false);
        }
      }
    };
    fetchDocuments();
  }, [open, employee?.id]);

  // Cleanup photo preview URL when component unmounts or preview changes
  useEffect(() => {
    return () => {
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  const handleChange = (field: keyof EmployeeCreate, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle primes change from PaieTab
  const handlePrimesChange = (
    newPrimesExonerees: { id?: number | null; prime_id: number; montant: number; date_debut?: string | null; date_fin?: string | null }[],
    newPrimesNonExonerees: { id?: number | null; prime_id: number; montant: number; nombre_jours?: number | null; mois?: number | null; annee?: number | null; date_debut?: string | null; date_fin?: string | null }[],
    newCotisationsSociales: { cotisation_id: number }[]
  ) => {
    setPrimesExonerees(newPrimesExonerees);
    setPrimesNonExonerees(newPrimesNonExonerees);
    setCotisationsSociales(newCotisationsSociales);
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError('Veuillez sélectionner une image valide');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('La taille de l\'image ne doit pas dépasser 5 MB');
      return;
    }

    if (employee) {
      // For existing employee, upload directly
      setPhotoUploading(true);
      try {
        const result = await employeesAPI.uploadPhoto(employee.id, file);
        setPhotoUrl(result.photo_url);
        setFormData((prev) => ({ ...prev, photo_url: result.photo_url }));
        showSuccess('Photo uploadée avec succès');
      } catch (error: any) {
        showError(extractErrorMessage(error));
      } finally {
        setPhotoUploading(false);
      }
    } else {
      // For new employee, store file for later upload after creation
      setPendingPhotoFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview);
      }
      setPhotoPreview(previewUrl);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePhotoDelete = async () => {
    if (employee && photoUrl) {
      // Delete from server for existing employee
      setPhotoUploading(true);
      try {
        await employeesAPI.deletePhoto(employee.id);
        setPhotoUrl(null);
        setFormData((prev) => ({ ...prev, photo_url: null }));
        showSuccess('Photo supprimée avec succès');
      } catch (error: any) {
        showError(extractErrorMessage(error));
      } finally {
        setPhotoUploading(false);
      }
    } else if (pendingPhotoFile) {
      // Just remove pending file for new employee
      setPendingPhotoFile(null);
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview);
        setPhotoPreview(null);
      }
    }
  };

  // Document upload handler
  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (allow common document types)
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/gif',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    if (!allowedTypes.includes(file.type)) {
      showError('Type de fichier non supporté. Utilisez PDF, images, Word ou Excel.');
      if (documentInputRef.current) documentInputRef.current.value = '';
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showError('La taille du fichier ne doit pas dépasser 10 MB');
      if (documentInputRef.current) documentInputRef.current.value = '';
      return;
    }

    // Validate title
    if (!newDocumentTitle.trim()) {
      showError('Veuillez saisir un titre pour le document');
      if (documentInputRef.current) documentInputRef.current.value = '';
      return;
    }

    if (employee?.id) {
      // For existing employee, upload directly
      setDocumentUploading(true);
      try {
        const newDoc = await employeesAPI.uploadDocument(
          employee.id,
          file,
          newDocumentType,
          newDocumentTitle.trim(),
          newDocumentDescription.trim() || undefined
        );
        setDocuments((prev) => [newDoc, ...prev]);
        // Reset form
        setNewDocumentTitle('');
        setNewDocumentDescription('');
        setNewDocumentType('CONTRAT');
        showSuccess('Document uploadé avec succès');
      } catch (error: any) {
        showError(extractErrorMessage(error));
      } finally {
        setDocumentUploading(false);
        if (documentInputRef.current) {
          documentInputRef.current.value = '';
        }
      }
    } else {
      // For new employee, add to pending documents
      const pendingDoc: PendingDocument = {
        id: `pending-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        documentType: newDocumentType,
        title: newDocumentTitle.trim(),
        description: newDocumentDescription.trim(),
      };
      setPendingDocuments((prev) => [pendingDoc, ...prev]);
      // Reset form
      setNewDocumentTitle('');
      setNewDocumentDescription('');
      setNewDocumentType('CONTRAT');
      showSuccess('Document ajouté (sera uploadé après la création)');
      if (documentInputRef.current) {
        documentInputRef.current.value = '';
      }
    }
  };

  // Remove pending document
  const handleRemovePendingDocument = (pendingId: string) => {
    setPendingDocuments((prev) => prev.filter((d) => d.id !== pendingId));
  };

  // Document delete handler
  const handleDocumentDelete = async (documentId: number) => {
    if (!employee?.id) return;
    try {
      await employeesAPI.deleteDocument(employee.id, documentId);
      setDocuments((prev) => prev.filter((d) => d.id !== documentId));
      showSuccess('Document supprimé avec succès');
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  // Format file size
  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async () => {
    // Show all validation errors
    setShowAllErrors(true);

    // Validation - check all required fields
    const errors: { field: string; tab: number; message: string }[] = [];

    // Tab 0: Personnel - ALL required
    if (!formData.nom_complet?.trim()) {
      errors.push({ field: 'nom_complet', tab: 0, message: 'Le nom complet est requis' });
    }
    if (!formData.date_naissance) {
      errors.push({ field: 'date_naissance', tab: 0, message: 'La date de naissance est requise' });
    }
    if (!formData.cin?.trim()) {
      errors.push({ field: 'cin', tab: 0, message: 'Le CIN est requis' });
    }
    if (!formData.telephone?.trim()) {
      errors.push({ field: 'telephone', tab: 0, message: 'Le contact est requis' });
    }
    if (!formData.adresse?.trim()) {
      errors.push({ field: 'adresse', tab: 0, message: 'L\'adresse est requise' });
    }

    // Tab 1: Contrat & Professionnel (merged)
    if (!formData.date_embauche) {
      errors.push({ field: 'date_embauche', tab: 1, message: 'La date d\'embauche est requise' });
    }
    if (!formData.fonction?.trim()) {
      errors.push({ field: 'fonction', tab: 1, message: 'La fonction est requise' });
    }
    if (!formData.numero_cnss?.trim()) {
      errors.push({ field: 'numero_cnss', tab: 1, message: 'Le numéro CNSS est requis' });
    }
    if (formData.type_contrat !== 'CDI') {
      if (!formData.duree_contrat_mois) {
        errors.push({ field: 'duree_contrat_mois', tab: 1, message: 'La durée du contrat est requise' });
      }
      if (!formData.date_fin_contrat) {
        errors.push({ field: 'date_fin_contrat', tab: 1, message: 'La date de fin de contrat est requise' });
      }
    }

    // Tab 2: Bancaire - ALL required
    if (!formData.banque?.trim()) {
      errors.push({ field: 'banque', tab: 2, message: 'La banque est requise' });
    }
    if (!formData.rib?.trim()) {
      errors.push({ field: 'rib', tab: 2, message: 'Le RIB est requis' });
    }

    // Tab 3: PAIE - salary fields required
    if (!formData.salaire_base) {
      errors.push({ field: 'salaire_base', tab: 3, message: 'Le salaire de base est requis' });
    }
    if (!formData.salaire_net) {
      errors.push({ field: 'salaire_net', tab: 3, message: 'Le salaire net est requis' });
    }
    if (!formData.declaration_journaliere) {
      errors.push({ field: 'declaration_journaliere', tab: 3, message: 'La déclaration journalière est requise' });
    }

    if (errors.length > 0) {
      // Navigate to the first tab with errors
      setTabValue(errors[0].tab);
      showError(`Veuillez remplir tous les champs requis (${errors.length} champ${errors.length > 1 ? 's' : ''} manquant${errors.length > 1 ? 's' : ''})`);
      return;
    }

    setLoading(true);
    try {
      // Clean data - remove empty strings for optional fields and parse price values
      const cleanData: EmployeeCreate | EmployeeUpdate = {
        ...formData,
        lieu_naissance: formData.lieu_naissance || null,
        nationalite: formData.nationalite || null,
        cin: formData.cin || null,
        adresse: formData.adresse || null,
        telephone: formData.telephone || null,
        fonction: formData.fonction || null,
        numero_cnss: formData.numero_cnss || null,
        banque: formData.banque || null,
        rib: formData.rib || null,
        salaire_base: formData.salaire_base ? parsePriceValue(formData.salaire_base) : null,
        salaire_net: formData.salaire_net ? parsePriceValue(formData.salaire_net) : null,
        declaration_journaliere: formData.declaration_journaliere ? parsePriceValue(formData.declaration_journaliere) : null,
        // Payroll calculated fields
        salaire_brut_global: formData.salaire_brut_global ? parsePriceValue(formData.salaire_brut_global) : null,
        salaire_brut_imposable: formData.salaire_brut_imposable ? parsePriceValue(formData.salaire_brut_imposable) : null,
        total_cotisations: formData.total_cotisations ? parsePriceValue(formData.total_cotisations) : null,
        frais_professionnel: formData.frais_professionnel ? parsePriceValue(formData.frais_professionnel) : null,
        salaire_net_imposable: formData.salaire_net_imposable ? parsePriceValue(formData.salaire_net_imposable) : null,
        ir_brut: formData.ir_brut ? parsePriceValue(formData.ir_brut) : null,
        deduction_charge_famille: formData.deduction_charge_famille ? parsePriceValue(formData.deduction_charge_famille) : null,
        ir_net: formData.ir_net ? parsePriceValue(formData.ir_net) : null,
      };

      if (employee) {
        await employeesAPI.update(employee.id, cleanData);
        // Save primes and cotisations for existing employee
        try {
          await employeePrimesAPI.update(employee.id, {
            primes_exonerees: primesExonerees,
            primes_non_exonerees: primesNonExonerees,
            cotisations_sociales: cotisationsSociales,
          });
        } catch (primesError: any) {
          console.error('Primes/cotisations update failed:', primesError);
        }
        showSuccess('Employé modifié avec succès');
      } else {
        // Create the employee first
        const newEmployee = await employeesAPI.create(cleanData as EmployeeCreate);

        // If there's a pending photo, upload it after creation
        if (pendingPhotoFile && newEmployee.id) {
          try {
            await employeesAPI.uploadPhoto(newEmployee.id, pendingPhotoFile);
          } catch (photoError: any) {
            // Employee created but photo upload failed - show warning but don't fail
            console.error('Photo upload failed:', photoError);
          }
        }

        // Upload pending documents after creation
        if (pendingDocuments.length > 0 && newEmployee.id) {
          const failedDocs: string[] = [];
          for (const pendingDoc of pendingDocuments) {
            try {
              await employeesAPI.uploadDocument(
                newEmployee.id,
                pendingDoc.file,
                pendingDoc.documentType,
                pendingDoc.title,
                pendingDoc.description || undefined
              );
            } catch (docError: any) {
              console.error('Document upload failed:', docError);
              failedDocs.push(pendingDoc.title);
            }
          }
          if (failedDocs.length > 0) {
            showError(`Employé créé mais ${failedDocs.length} document(s) n'ont pas pu être uploadés: ${failedDocs.join(', ')}`);
            clearDraft();
            onSuccess();
            return;
          }
        }

        // Save primes and cotisations for new employee
        if ((primesExonerees.length > 0 || primesNonExonerees.length > 0 || cotisationsSociales.length > 0) && newEmployee.id) {
          try {
            await employeePrimesAPI.update(newEmployee.id, {
              primes_exonerees: primesExonerees,
              primes_non_exonerees: primesNonExonerees,
              cotisations_sociales: cotisationsSociales,
            });
          } catch (primesError: any) {
            console.error('Primes/cotisations save failed:', primesError);
          }
        }

        showSuccess('Employé créé avec succès');
      }
      clearDraft();
      onSuccess();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // X / Annuler = deliberate cancel: discard any draft, then close.
  const handleClose = () => {
    clearDraft();
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (!employee) {
        saveDraft({ formData, primesExonerees, primesNonExonerees, cotisationsSociales });
      }
      onClose();
    }
  };

  const isReadOnly = viewMode && !isEditing;

  return (
    <>
    <Dialog
      open={dialogOpen}
      onClose={handleDialogClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={SlideUpTransition}
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: HR_COLOR,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Small Avatar in Title */}
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={photoPreview || getFullFileUrl(photoUrl)}
              onClick={photoPreview || photoUrl ? () => setPhotoPreviewOpen(true) : undefined}
              sx={{
                width: 50,
                height: 50,
                backgroundColor: 'rgba(255,255,255,0.2)',
                fontSize: '1rem',
                border: '2px solid rgba(255,255,255,0.3)',
                cursor: photoPreview || photoUrl ? 'pointer' : 'default',
              }}
            >
              {formData.nom_complet?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </Avatar>
            {!isReadOnly && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handlePhotoUpload}
                />
                {/* Upload photo button - bottom right */}
                <IconButton
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoUploading}
                  sx={{
                    position: 'absolute',
                    bottom: -4,
                    right: -4,
                    backgroundColor: 'white',
                    color: HR_COLOR,
                    width: 22,
                    height: 22,
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  {photoUploading ? (
                    <CircularProgress size={12} sx={{ color: HR_COLOR }} />
                  ) : (
                    <PhotoIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
                {/* Delete photo button - top left (only when photo exists) */}
                {(photoPreview || photoUrl) && (
                  <IconButton
                    size="small"
                    onClick={handlePhotoDelete}
                    disabled={photoUploading}
                    sx={{
                      position: 'absolute',
                      top: -4,
                      left: -4,
                      backgroundColor: '#f44336',
                      color: 'white',
                      width: 18,
                      height: 18,
                      '&:hover': { backgroundColor: '#d32f2f' },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                )}
              </>
            )}
          </Box>
          <Typography variant="h6">
            {employee
              ? viewMode
                ? `Fiche: ${employee.nom_complet}`
                : `Modifier: ${employee.nom_complet}`
              : 'Nouvel Employé'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Status info on the right */}
          {employee ? (
            <Box sx={{ textAlign: 'right' }}>
              {employee.type_contrat !== 'STAGE' ? (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                  {employee.matricule}
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontStyle: 'italic' }}>
                  Stagiaire (pas de matricule)
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>
                Ancienneté: {employee.anciennete}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'right' }}>
              {formData.type_contrat === 'STAGE' ? (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontStyle: 'italic' }}>
                  Stagiaire (pas de matricule)
                </Typography>
              ) : (
                <>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>
                    Matricule généré automatiquement:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                    {nextMatricule || '...'}
                  </Typography>
                </>
              )}
            </Box>
          )}
          {viewMode && !isEditing && (
            <Tooltip title="Modifier">
              <IconButton onClick={() => setIsEditing(true)} sx={{ color: 'white' }}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          <MinimizeButton color="white" onClick={minimize} />
          <IconButton onClick={handleClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                color: HR_COLOR,
                minHeight: 40,
                py: 0.5,
                px: 1.5,
                fontSize: '0.8rem',
                textTransform: 'uppercase',
              },
              '& .Mui-selected': { color: HR_COLOR },
              '& .MuiTabs-indicator': { backgroundColor: HR_COLOR },
              '& .MuiTab-iconWrapper': { marginRight: 0.5 },
            }}
          >
            <Tab
              icon={
                tabErrors[0] ? (
                  <Badge badgeContent={<ErrorIcon sx={{ fontSize: 12, color: '#f44336' }} />}>
                    <PersonIcon sx={{ fontSize: 18 }} />
                  </Badge>
                ) : (
                  <PersonIcon sx={{ fontSize: 18 }} />
                )
              }
              label="Personnel"
              iconPosition="start"
              sx={tabErrors[0] ? { color: '#f44336 !important' } : {}}
            />
            <Tab
              icon={
                tabErrors[1] ? (
                  <Badge badgeContent={<ErrorIcon sx={{ fontSize: 12, color: '#f44336' }} />}>
                    <ContractIcon sx={{ fontSize: 18 }} />
                  </Badge>
                ) : (
                  <ContractIcon sx={{ fontSize: 18 }} />
                )
              }
              label="Professionnel/Contrat"
              iconPosition="start"
              sx={tabErrors[1] ? { color: '#f44336 !important' } : {}}
            />
            <Tab
              icon={
                tabErrors[2] ? (
                  <Badge badgeContent={<ErrorIcon sx={{ fontSize: 12, color: '#f44336' }} />}>
                    <BankIcon sx={{ fontSize: 18 }} />
                  </Badge>
                ) : (
                  <BankIcon sx={{ fontSize: 18 }} />
                )
              }
              label="Bancaire"
              iconPosition="start"
              sx={tabErrors[2] ? { color: '#f44336 !important' } : {}}
            />
            <Tab
              icon={
                tabErrors[3] ? (
                  <Badge badgeContent={<ErrorIcon sx={{ fontSize: 12, color: '#f44336' }} />}>
                    <PaieIcon sx={{ fontSize: 18 }} />
                  </Badge>
                ) : (
                  <PaieIcon sx={{ fontSize: 18 }} />
                )
              }
              label="PAIE"
              iconPosition="start"
              sx={tabErrors[3] ? { color: '#f44336 !important' } : {}}
            />
          </Tabs>
        </Box>

        <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
          {/* Tab 1: Personal Info */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Nom Complet"
                  value={formData.nom_complet}
                  onChange={(e) => handleChange('nom_complet', e.target.value)}
                  onBlur={() => handleBlur('nom_complet')}
                  fullWidth
                  required
                  disabled={isReadOnly}
                  placeholder="Ex: Ahmed El Alami"
                  error={hasError('nom_complet')}
                  helperText={getErrorText('nom_complet')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date de naissance"
                  type="date"
                  value={formData.date_naissance || ''}
                  onChange={(e) => handleChange('date_naissance', e.target.value || null)}
                  onBlur={() => handleBlur('date_naissance')}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  disabled={isReadOnly}
                  error={hasError('date_naissance')}
                  helperText={getErrorText('date_naissance')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Lieu de Naissance"
                  value={formData.lieu_naissance}
                  onChange={(e) => handleChange('lieu_naissance', e.target.value)}
                  fullWidth
                  disabled={isReadOnly}
                  placeholder="Ex: Casablanca"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nationalité"
                  value={formData.nationalite}
                  onChange={(e) => handleChange('nationalite', e.target.value.toUpperCase())}
                  fullWidth
                  disabled={isReadOnly}
                  placeholder="Ex: MAROCAIN"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="CIN"
                  value={formData.cin}
                  onChange={(e) => handleChange('cin', e.target.value.toUpperCase())}
                  onBlur={() => handleBlur('cin')}
                  fullWidth
                  required
                  disabled={isReadOnly}
                  placeholder="Ex: AB123456"
                  error={hasError('cin')}
                  helperText={getErrorText('cin')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={isReadOnly} required error={hasError('situation_familiale')}>
                  <InputLabel>Situation Familiale</InputLabel>
                  <Select
                    value={formData.situation_familiale}
                    onChange={(e) => handleChange('situation_familiale', e.target.value as SituationFamiliale)}
                    onBlur={() => handleBlur('situation_familiale')}
                    label="Situation Familiale"
                  >
                    {SITUATION_FAMILIALE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nombre d'enfants"
                  type="number"
                  value={formData.nbr_enfants}
                  onChange={(e) => handleChange('nbr_enfants', parseInt(e.target.value) || 0)}
                  onBlur={() => handleBlur('nbr_enfants')}
                  fullWidth
                  required
                  disabled={isReadOnly}
                  InputProps={{ inputProps: { min: 0 } }}
                  error={hasError('nbr_enfants')}
                  helperText={getErrorText('nbr_enfants')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Contact (Téléphone)"
                  value={formData.telephone}
                  onChange={(e) => handleChange('telephone', e.target.value)}
                  onBlur={() => handleBlur('telephone')}
                  fullWidth
                  required
                  disabled={isReadOnly}
                  placeholder="Ex: 0612345678"
                  error={hasError('telephone')}
                  helperText={getErrorText('telephone')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Adresse"
                  value={formData.adresse}
                  onChange={(e) => handleChange('adresse', e.target.value)}
                  onBlur={() => handleBlur('adresse')}
                  fullWidth
                  required
                  disabled={isReadOnly}
                  error={hasError('adresse')}
                  helperText={getErrorText('adresse')}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    المعلومات بالعربية (Informations en Arabe)
                  </Typography>
                </Divider>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="الاسم الكامل (Nom Complet en Arabe)"
                  value={formData.nom_complet_ar}
                  onChange={(e) => handleChange('nom_complet_ar', e.target.value)}
                  fullWidth
                  disabled={isReadOnly}
                  placeholder="مثال: أحمد العلمي"
                  inputProps={{
                    dir: 'rtl',
                    style: { textAlign: 'right' }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="مكان الميلاد (Lieu de Naissance en Arabe)"
                  value={formData.lieu_naissance_ar}
                  onChange={(e) => handleChange('lieu_naissance_ar', e.target.value)}
                  fullWidth
                  disabled={isReadOnly}
                  placeholder="مثال: الدار البيضاء"
                  inputProps={{
                    dir: 'rtl',
                    style: { textAlign: 'right' }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="العنوان (Adresse en Arabe)"
                  value={formData.adresse_ar}
                  onChange={(e) => handleChange('adresse_ar', e.target.value)}
                  fullWidth
                  disabled={isReadOnly}
                  inputProps={{
                    dir: 'rtl',
                    style: { textAlign: 'right' }
                  }}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 2: Contract & Professional Info (merged) */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              {/* Professional Info Section */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Informations Professionnelles
                  </Typography>
                </Divider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date d'embauche"
                  type="date"
                  value={formData.date_embauche}
                  onChange={(e) => handleChange('date_embauche', e.target.value)}
                  onBlur={() => handleBlur('date_embauche')}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  disabled={isReadOnly}
                  error={hasError('date_embauche')}
                  helperText={getErrorText('date_embauche')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fonction / Poste"
                  value={formData.fonction}
                  onChange={(e) => handleChange('fonction', e.target.value)}
                  onBlur={() => handleBlur('fonction')}
                  fullWidth
                  required
                  disabled={isReadOnly}
                  error={hasError('fonction')}
                  helperText={getErrorText('fonction')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={isReadOnly} required error={hasError('statut')}>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.statut}
                    onChange={(e) => handleChange('statut', e.target.value as StatutEmploye)}
                    onBlur={() => handleBlur('statut')}
                    label="Statut"
                  >
                    {STATUT_EMPLOYE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Numéro CNSS"
                  value={formData.numero_cnss}
                  onChange={(e) => handleChange('numero_cnss', e.target.value)}
                  onBlur={() => handleBlur('numero_cnss')}
                  fullWidth
                  required
                  disabled={isReadOnly}
                  error={hasError('numero_cnss')}
                  helperText={getErrorText('numero_cnss')}
                />
              </Grid>

              {/* Contract Info Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Informations du Contrat
                  </Typography>
                </Divider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={isReadOnly} required error={hasError('type_contrat')}>
                  <InputLabel>Type de Contrat</InputLabel>
                  <Select
                    value={formData.type_contrat}
                    onChange={(e) => handleChange('type_contrat', e.target.value as TypeContrat)}
                    onBlur={() => handleBlur('type_contrat')}
                    label="Type de Contrat"
                  >
                    {TYPE_CONTRAT_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Durée du contrat (mois)"
                  type="number"
                  value={formData.duree_contrat_mois || ''}
                  onChange={(e) => handleChange('duree_contrat_mois', parseInt(e.target.value) || null)}
                  onBlur={() => handleBlur('duree_contrat_mois')}
                  fullWidth
                  required={formData.type_contrat !== 'CDI'}
                  disabled={isReadOnly || formData.type_contrat === 'CDI'}
                  InputProps={{ inputProps: { min: 1 } }}
                  error={hasError('duree_contrat_mois')}
                  helperText={formData.type_contrat === 'CDI' ? 'Non applicable pour CDI' : getErrorText('duree_contrat_mois')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date de fin de contrat"
                  type="date"
                  value={formData.date_fin_contrat || ''}
                  onChange={(e) => handleChange('date_fin_contrat', e.target.value || null)}
                  onBlur={() => handleBlur('date_fin_contrat')}
                  fullWidth
                  required={formData.type_contrat !== 'CDI'}
                  InputLabelProps={{ shrink: true }}
                  disabled={isReadOnly || formData.type_contrat === 'CDI'}
                  error={hasError('date_fin_contrat')}
                  helperText={formData.type_contrat === 'CDI' ? 'Non applicable pour CDI' : getErrorText('date_fin_contrat')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={isReadOnly}>
                  <InputLabel>Relance Contrat</InputLabel>
                  <Select
                    value={formData.relance_contrat ? 'true' : 'false'}
                    onChange={(e) => handleChange('relance_contrat', e.target.value === 'true')}
                    label="Relance Contrat"
                  >
                    <MenuItem value="false">Non</MenuItem>
                    <MenuItem value="true">Oui - Alerter avant expiration</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Departure / Solde de Tout Compte — shown when the employee is Inactif */}
              {formData.statut === 'INACTIF' && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }}>
                      <Typography variant="body2" color="textSecondary">
                        Sortie / Solde de Tout Compte
                      </Typography>
                    </Divider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Date de sortie"
                      type="date"
                      value={formData.date_sortie || ''}
                      onChange={(e) => handleChange('date_sortie', e.target.value || null)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled={isReadOnly}
                      helperText="Utilisée pour le calcul du STC (prorata, ancienneté)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={isReadOnly}>
                      <InputLabel>Motif de départ</InputLabel>
                      <Select
                        value={formData.motif_sortie || ''}
                        onChange={(e) => handleChange('motif_sortie', (e.target.value || null) as MotifSortie | null)}
                        label="Motif de départ"
                      >
                        <MenuItem value="">
                          <em>Non renseigné</em>
                        </MenuItem>
                        {MOTIF_SORTIE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {/* Documents Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Documents
                  </Typography>
                </Divider>
              </Grid>

              {/* Upload Form - Show for both new and existing employees */}
              {!isReadOnly && (
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: 'rgba(0, 137, 123, 0.03)',
                      borderColor: HR_COLOR,
                      borderStyle: 'dashed',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 2, color: HR_COLOR }}>
                      Ajouter un document
                    </Typography>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Type de document</InputLabel>
                          <Select
                            value={newDocumentType}
                            onChange={(e) => setNewDocumentType(e.target.value as DocumentType)}
                            label="Type de document"
                          >
                            {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Titre du document"
                          value={newDocumentTitle}
                          onChange={(e) => setNewDocumentTitle(e.target.value)}
                          fullWidth
                          size="small"
                          required
                          placeholder="Ex: CDI - Janvier 2024"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Description (optionnel)"
                          value={newDocumentDescription}
                          onChange={(e) => setNewDocumentDescription(e.target.value)}
                          fullWidth
                          size="small"
                          placeholder="Notes supplémentaires..."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <input
                          ref={documentInputRef}
                          type="file"
                          hidden
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                          onChange={handleDocumentUpload}
                        />
                        <Button
                          variant="outlined"
                          startIcon={documentUploading ? <CircularProgress size={16} /> : <UploadIcon />}
                          onClick={() => documentInputRef.current?.click()}
                          disabled={documentUploading || !newDocumentTitle.trim()}
                          sx={{
                            color: HR_COLOR,
                            borderColor: HR_COLOR,
                            '&:hover': { borderColor: '#00695C', backgroundColor: 'rgba(0, 137, 123, 0.08)' },
                          }}
                        >
                          {documentUploading ? 'Upload en cours...' : employee ? 'Choisir et uploader un fichier' : 'Ajouter un fichier'}
                        </Button>
                        <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
                          PDF, Word, Excel, Images (max 10 MB)
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}

              {/* Pending Documents List - For new employees */}
              {!employee && pendingDocuments.length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    {pendingDocuments.length} document(s) sera(ont) uploadé(s) après la création de l'employé
                  </Alert>
                  <Box>
                    {pendingDocuments.map((doc) => (
                      <Paper
                        key={doc.id}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: 'rgba(0, 137, 123, 0.05)',
                          '&:hover': { backgroundColor: 'rgba(0, 137, 123, 0.08)' },
                        }}
                      >
                        <FileIcon sx={{ color: HR_COLOR, mr: 1.5 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                              {doc.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                backgroundColor: 'rgba(0, 137, 123, 0.1)',
                                color: HR_COLOR,
                              }}
                            >
                              {DOCUMENT_TYPE_OPTIONS.find((o) => o.value === doc.documentType)?.label || doc.documentType}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                                color: '#f57c00',
                              }}
                            >
                              En attente
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            {doc.file.name} • {formatFileSize(doc.file.size)}
                          </Typography>
                          {doc.description && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              {doc.description}
                            </Typography>
                          )}
                        </Box>
                        <Tooltip title="Retirer">
                          <IconButton
                            size="small"
                            onClick={() => handleRemovePendingDocument(doc.id)}
                            sx={{ color: '#f44336' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Paper>
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Documents List - For existing employees */}
              {employee && (
                <Grid item xs={12}>
                    {documentsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} sx={{ color: HR_COLOR }} />
                      </Box>
                    ) : documents.length === 0 ? (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Aucun document attaché à cet employé.
                      </Alert>
                    ) : (
                      <Box sx={{ mt: 1 }}>
                        {documents.map((doc) => (
                          <Paper
                            key={doc.id}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              mb: 1,
                              display: 'flex',
                              alignItems: 'center',
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                            }}
                          >
                            <FileIcon sx={{ color: HR_COLOR, mr: 1.5 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                                  {doc.title}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    backgroundColor: 'rgba(0, 137, 123, 0.1)',
                                    color: HR_COLOR,
                                  }}
                                >
                                  {DOCUMENT_TYPE_OPTIONS.find((o) => o.value === doc.document_type)?.label || doc.document_type}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="textSecondary">
                                {doc.file_name} {doc.file_size ? `• ${formatFileSize(doc.file_size)}` : ''}
                                {' • '}
                                {new Date(doc.created_at).toLocaleDateString('fr-FR')}
                              </Typography>
                              {doc.description && (
                                <Typography variant="caption" color="textSecondary" display="block">
                                  {doc.description}
                                </Typography>
                              )}
                            </Box>
                            <Tooltip title="Ouvrir">
                              <IconButton
                                size="small"
                                onClick={() => window.open(`${API_BASE_URL}${doc.file_url}`, '_blank')}
                                sx={{ color: HR_COLOR }}
                              >
                                <OpenIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!isReadOnly && (
                              <Tooltip title="Supprimer">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDocumentDelete(doc.id)}
                                  sx={{ color: '#f44336' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Paper>
                        ))}
                      </Box>
                    )}
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* Tab 3: Banking */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Informations Bancaires
                  </Typography>
                </Divider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="RIB"
                  value={formData.rib}
                  onChange={(e) => {
                    const newRIB = e.target.value;
                    handleChange('rib', newRIB);
                    // Auto-detect bank from RIB
                    const detectedBank = detectBankFromRIB(newRIB);
                    if (detectedBank) {
                      handleChange('banque', detectedBank);
                    }
                  }}
                  onBlur={() => handleBlur('rib')}
                  fullWidth
                  required
                  disabled={isReadOnly}
                  placeholder="24 chiffres"
                  error={hasError('rib')}
                  helperText={getErrorText('rib') || 'La banque sera détectée automatiquement'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Banque"
                  value={formData.banque}
                  onChange={(e) => handleChange('banque', e.target.value)}
                  onBlur={() => handleBlur('banque')}
                  fullWidth
                  required
                  disabled={isReadOnly}
                  placeholder="Détection automatique via RIB"
                  error={hasError('banque')}
                  helperText={getErrorText('banque') || (formData.banque ? 'Détectée automatiquement' : 'Entrez le RIB pour détecter la banque')}
                  InputProps={{
                    sx: formData.banque ? { backgroundColor: 'rgba(0, 137, 123, 0.08)' } : {},
                  }}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 4: PAIE */}
          <TabPanel value={tabValue} index={3}>
            <PaieTab
              employeeId={employee?.id || null}
              dateEmbauche={formData.date_embauche || null}
              salaireBase={formData.salaire_base ?? null}
              declarationJournaliere={formData.declaration_journaliere ?? null}
              nbrEnfants={formData.nbr_enfants ?? 0}
              situationFamiliale={formData.situation_familiale || 'CELIBATAIRE'}
              isReadOnly={isReadOnly}
              onSalaireChange={(field, value) => {
                handleChange(field as keyof EmployeeCreate, value);
              }}
              onPrimesChange={handlePrimesChange}
              hasError={hasError}
              getErrorText={getErrorText}
            />
          </TabPanel>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, backgroundColor: 'background.default' }}>
        <Button onClick={handleClose} color="inherit">
          {isReadOnly ? 'Fermer' : 'Annuler'}
        </Button>
        {!isReadOnly && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{
              backgroundColor: HR_COLOR,
              '&:hover': { backgroundColor: '#00695C' },
            }}
          >
            {employee ? 'Enregistrer' : 'Créer'}
          </Button>
        )}
      </DialogActions>

      {/* Alert Dialog for error/success messages */}
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        showCancel={alertState.showCancel}
      />

      {/* Full-size photo preview */}
      <ImagePreviewModal
        open={photoPreviewOpen}
        imageUrl={photoPreview || getFullFileUrl(photoUrl) || ''}
        imageName={formData.nom_complet ? `${formData.nom_complet}.jpg` : 'photo.jpg'}
        onClose={() => setPhotoPreviewOpen(false)}
      />
    </Dialog>

    <MinimizedBar
      open={open && minimized}
      color="#009688"
      title={
        employee
          ? viewMode
            ? `Fiche: ${employee.nom_complet}`
            : `Modifier: ${employee.nom_complet}`
          : 'Nouvel Employé'
      }
      onRestore={restore}
      onClose={handleClose}
    />
    </>
  );
};

export default EmployeeFormDialog;
